import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Code2, Globe, ChevronDown, CheckCircle, ChevronUp, HelpCircle
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import Navbar from './Navbar';
import Footer from './Footer';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5%;
  background: ${props => props.scrolled ? 'var(--nav-bg)' : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(12px)' : 'none'};
  border-bottom: ${props => props.scrolled ? '1px solid var(--glass-border)' : 'none'};
  transition: all 0.3s ease;
  z-index: 1000;
`;

const Logo = styled(Link)`
  font-family: 'Outfit', sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--text-main);
  
  span {
    background: linear-gradient(to right, var(--primary), var(--secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 30px;
  align-items: center;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-muted);
  transition: color 0.3s;
  
  &:hover {
    color: var(--text-main);
  }
`;

const PageContainer = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: var(--bg-dark);
  color: var(--text-main);
`;

const ContentSection = styled.section`
  padding: 80px 5%;
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2rem, 5vw, 3rem);
  margin-bottom: 40px;
  text-align: center;
`;

const FAQItem = styled(motion.div)`
  margin-bottom: 20px;
  border: 1px solid var(--glass-border);
  border-radius: 12px;
  background: var(--bg-card);
  overflow: hidden;
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  border: none;
  color: var(--text-main);
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  
  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const FAQAnswer = styled(motion.div)`
  padding: 0 20px;
  color: var(--text-muted);
  line-height: 1.6;
  overflow: hidden;
`;

const LangContainer = styled.div`
  position: relative;
  margin-left: 20px;
`;

const LangButton = styled.button`
  background: transparent;
  border: 1px solid var(--glass-border);
  color: var(--text-main);
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  transition: all 0.3s;
  
  &:hover {
    border-color: var(--primary);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const LangMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 10px;
  background: var(--bg-card);
  border: 1px solid var(--glass-border);
  border-radius: 12px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
  backdrop-filter: blur(12px);
  overflow: hidden;
`;

const LangItem = styled.button`
  background: transparent;
  border: none;
  color: var(--text-muted);
  padding: 8px 12px;
  text-align: left;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: white;
  }
  
  ${props => props.active && `
    background: rgba(99, 102, 241, 0.1);
    color: var(--primary);
  `}
`;

const FAQ = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [faqData, setFaqData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);

    fetch(`${process.env.REACT_APP_API_URL || ''}/api/faqs`)
      .then(res => res.json())
      .then(data => {
        const sorted = (Array.isArray(data) ? data : []).sort((a, b) => a.sort_order - b.sort_order);
        setFaqData(sorted);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <>
      <Navbar />

      <PageContainer>
        <ContentSection>
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {t('faq.title')}
          </Title>

          <div style={{ marginBottom: '60px' }}>
            {faqData.map((faq, index) => {
              return (
                <FAQItem
                  key={faq.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <FAQQuestion onClick={() => toggleFAQ(index)}>
                    {faq.question}
                    {activeIndex === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </FAQQuestion>
                  <AnimatePresence>
                    {activeIndex === index && (
                      <FAQAnswer
                        initial={{ height: 0, opacity: 0, marginBottom: 0 }}
                        animate={{ height: "auto", opacity: 1, marginBottom: 20 }}
                        exit={{ height: 0, opacity: 0, marginBottom: 0 }}
                      >
                        <div style={{ whiteSpace: 'pre-line' }}>{faq.answer}</div>
                      </FAQAnswer>
                    )}
                  </AnimatePresence>
                </FAQItem>
              );
            })}
            {loading && <div style={{ textAlign: 'center', width: '100%' }}>Loading FAQs...</div>}
            {!loading && faqData.length === 0 && <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No FAQs posted yet.</div>}
          </div>
        </ContentSection>
      </PageContainer>
      <Footer />
    </>
  );
};

export default FAQ;
